import React, { useMemo } from "react";
import { Container, Row, Col } from "react-bootstrap";
import Particle from "../Particle";
import { 
  FaBuilding, 
  FaCalendarAlt, 
  FaMapMarkerAlt, 
  FaAward,
  FaTrophy,
  FaChartLine,
  FaUsers,
  FaGem
} from "react-icons/fa";

function ProfessionalExperience() {
  // Memoize the experiences data
  const experiences = useMemo(() => [
    {
      id: 1,
      company: "ClickFortify",
      position: "Senior Software Engineer",
      duration: "January 2025 - Present",
      location: "Remote",
      badge: "Leadership",
      priority: "high",
      description: "Leading development of comprehensive ad fraud protection platform that automatically blocks invalid traffic from Google Ads and Facebook Ads in real-time.",
      responsibilities: [
        "Architected and developed comprehensive ad fraud protection system processing 10M+ daily transactions",
        "Implemented advanced bot detection algorithms with machine learning and behavioral analysis",
        "Built competitor click monitoring and automated protection systems",
        "Developed custom Kubernetes operators for scalable infrastructure management",
        "Achieved 40% cloud cost reduction through infrastructure optimization",
        "Integrated OpenTelemetry for comprehensive observability and monitoring",
        "Implemented vector databases for efficient fraud pattern storage and retrieval"
      ],
      technologies: ["Node.js", "Python", "GCP", "Kubernetes", "OpenTelemetry", "Vector Databases", "TypeScript", "Docker", "PostgreSQL", "Redis", "Apache Kafka"],
      achievements: ["Protected $100M+ in ad spend", "40% reduction in invalid clicks", "99.9% fraud detection accuracy", "90% reduction in competitor click fraud"],
      stats: { transactions: "10M+", accuracy: "99.9%", savings: "$100M+", reduction: "40%" }
    },
    {
      id: 2,
      company: "Masdr - مصدر",
      position: "Backend Engineer",
      duration: "June 2025 - Present",
      location: "Saudi Arabia Remote",
      badge: "Enterprise",
      priority: "high",
      description: "Developing enterprise payroll application for 50,000+ employees with comprehensive HR management and government compliance features.",
      responsibilities: [
        "Built enterprise-grade payroll system handling 50,000+ employee records",
        "Implemented GOSI and ZATCA integration for government compliance",
        "Developed multi-tenant architecture with isolated data management",
        "Created comprehensive API suite with 85% code coverage",
        "Implemented secure authentication with JWT and OAuth2",
        "Built real-time notification system for payroll processing",
        "Developed automated tax calculation and reporting systems"
      ],
      technologies: ["Java Spring Boot", "PostgreSQL", "Redis", "Docker", "Jenkins", "RESTful APIs", "Microservices", "JWT", "OAuth2", "Swagger"],
      achievements: ["50,000+ employees managed", "85% code coverage", "Zero downtime deployments", "Comprehensive government compliance"],
      stats: { employees: "50K+", coverage: "85%", uptime: "100%", compliance: "100%" }
    },
    {
      id: 3,
      company: "Southville Solutions",
      position: "Senior Software Engineer",
      duration: "February 2023 - Present",
      location: "Lahore, Pakistan",
      badge: "Architecture",
      priority: "medium",
      description: "Leading development of enterprise-grade booking, scheduling, and HR systems with multi-tenant architecture and real-time processing capabilities.",
      responsibilities: [
        "Developed enterprise-grade booking and scheduling systems handling 10,000+ daily transactions",
        "Designed multi-tenant payroll and HR systems managing 1000+ isolated tenant databases",
        "Built robust backend services using Java Spring Boot for financial transactions",
        "Implemented secure JMS-based event pipelines for salary disbursements and attendance processing",
        "Established centralized logging and monitoring systems reducing incident response time by 60%",
        "Led mobile application development with React Native achieving 95% crash-free sessions",
        "Architected microservices infrastructure breaking down monolithic applications into 12 manageable services"
      ],
      technologies: ["Java Spring Boot", "Nest.js", "MongoDB", "Oracle", "JMS", "AWS CloudWatch", "React Native", "Redis", "Docker"],
      achievements: ["10,000+ daily transactions", "60% faster incident response", "95% crash-free mobile sessions", "40% response time optimization"],
      stats: { transactions: "10K+", response: "60%", sessions: "95%", optimization: "40%" }
    },
    {
      id: 4,
      company: "Stack Intel.io",
      position: "Associate Software Engineer",
      duration: "February 2021 - February 2023",
      location: "Lahore, Pakistan",
      badge: "Performance",
      priority: "medium",
      description: "Developed high-performance real-time applications and responsive user interfaces with focus on user engagement and payment processing.",
      responsibilities: [
        "Built high-performance real-time chat application supporting 10,000+ concurrent users",
        "Developed responsive user interfaces with React.js and Redux improving engagement by 25%",
        "Integrated Stripe API for payment processing handling $500K+ in transactions",
        "Collaborated with UI/UX design team reducing user drop-off rates by 35%",
        "Implemented automated testing suites achieving 85% code coverage",
        "Optimized system performance and user experience across all devices",
        "Maintained zero security incidents across payment systems"
      ],
      technologies: ["Node.js", "Express.js", "React.js", "Redux", "WebSockets", "Stripe API", "Tailwind CSS", "MongoDB", "Redis"],
      achievements: ["10,000+ concurrent users", "25% user engagement improvement", "35% reduction in drop-off rates", "85% code coverage", "Zero security incidents"],
      stats: { users: "10K+", engagement: "25%", reduction: "35%", coverage: "85%" }
    }
  ], []);

  const getBadgeColor = (badge) => {
    const colors = {
      "Leadership": "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      "Enterprise": "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      "Architecture": "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      "Performance": "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
    };
    return colors[badge] || "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";
  };

  const getPriorityColor = (priority) => {
    return priority === "high" ? "#ff6b6b" : priority === "medium" ? "#4ecdc4" : "#45b7d1";
  };

  return (
    <Container fluid className="professional-experience-section" id="professional-experience">
      <Particle />
      <Container>
        <div style={{ textAlign: "center", marginBottom: "60px" }}>
          <div style={{ 
            display: "inline-flex", 
            alignItems: "center", 
            gap: "15px",
            marginBottom: "20px",
            padding: "15px 30px",
            background: "linear-gradient(135deg, rgba(199, 112, 240, 0.1) 0%, rgba(56, 249, 215, 0.1) 100%)",
            borderRadius: "50px",
            border: "1px solid rgba(199, 112, 240, 0.2)"
          }}>
            <FaTrophy style={{ fontSize: "1.5em", color: "#c770f0" }} />
            <span style={{ color: "#c770f0", fontSize: "1.1em", fontWeight: "600" }}>Professional Journey</span>
            <FaGem style={{ fontSize: "1.2em", color: "#38f9d7" }} />
          </div>
          
          <h1 className="project-heading" style={{ 
            fontSize: "3.5em", 
            marginBottom: "20px",
            background: "linear-gradient(135deg, #c770f0 0%, #38f9d7 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text"
          }}>
            Professional <strong>Experience</strong>
          </h1>
          
          <p style={{ 
            fontSize: "1.3em", 
            color: "rgba(255, 255, 255, 0.9)",
            maxWidth: "800px",
            margin: "0 auto",
            lineHeight: "1.6"
          }}>
            Proven track record of delivering enterprise-scale solutions with measurable business impact across diverse industries and technologies.
          </p>
        </div>

        <Row style={{ justifyContent: "center", paddingBottom: "10px" }}>
          {experiences.map((experience) => (
            <Col md={6} lg={6} className="experience-card" key={experience.id} style={{ marginBottom: "40px" }}>
              <div 
                className="experience-card-inner"
                style={{
                  background: "linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "20px",
                  padding: "35px",
                  color: "white",
                  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                  height: "100%",
                  position: "relative",
                  overflow: "hidden",
                  cursor: "pointer"
                }}
                onMouseEnter={(e) => {
                  const card = e.currentTarget;
                  card.style.transform = "translateY(-3px) scale(1.01)";
                  card.style.boxShadow = "0 12px 25px rgba(199, 112, 240, 0.2)";
                  card.style.borderColor = "rgba(199, 112, 240, 0.3)";
                  card.style.background = "linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.03) 100%)";
                }}
                onMouseLeave={(e) => {
                  const card = e.currentTarget;
                  card.style.transform = "translateY(0) scale(1)";
                  card.style.boxShadow = "0 8px 20px rgba(0, 0, 0, 0.15)";
                  card.style.borderColor = "rgba(255, 255, 255, 0.1)";
                  card.style.background = "linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)";
                }}
              >
                
                {/* Priority Indicator */}
                <div style={{
                  position: "absolute",
                  top: "15px",
                  right: "15px",
                  width: "12px",
                  height: "12px",
                  borderRadius: "50%",
                  backgroundColor: getPriorityColor(experience.priority),
                  boxShadow: `0 0 10px ${getPriorityColor(experience.priority)}`
                }} />

                {/* Badge */}
                {experience.badge && (
                  <div style={{
                    position: "absolute",
                    top: "15px",
                    left: "15px",
                    padding: "6px 12px",
                    borderRadius: "15px",
                    fontSize: "0.7em",
                    fontWeight: "600",
                    background: getBadgeColor(experience.badge),
                    color: "white",
                    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)"
                  }}>
                    {experience.badge}
                  </div>
                )}

                <div style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "25px",
                  marginTop: "10px"
                }}>
                  <div style={{
                    fontSize: "2.5em",
                    color: "#c770f0",
                    marginRight: "20px",
                    filter: "drop-shadow(0 0 15px rgba(199, 112, 240, 0.4))"
                  }}>
                    <FaBuilding />
                  </div>
                  <div>
                    <h3 style={{
                      fontSize: "1.6em",
                      fontWeight: "700",
                      margin: 0,
                      color: "#c770f0",
                      lineHeight: "1.2",
                      background: "linear-gradient(135deg, #c770f0 0%, #38f9d7 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text"
                    }}>
                      {experience.position}
                    </h3>
                    <h4 style={{
                      fontSize: "1.2em",
                      fontWeight: "600",
                      margin: "8px 0",
                      color: "rgba(255, 255, 255, 0.95)",
                      lineHeight: "1.2"
                    }}>
                      {experience.company}
                    </h4>
                  </div>
                </div>

                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "20px",
                  marginBottom: "25px",
                  flexWrap: "wrap"
                }}>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "8px 15px",
                    background: "rgba(199, 112, 240, 0.1)",
                    borderRadius: "20px",
                    border: "1px solid rgba(199, 112, 240, 0.2)"
                  }}>
                    <FaCalendarAlt style={{ color: "#38f9d7", fontSize: "0.9em" }} />
                    <span style={{ fontSize: "0.9em", color: "rgba(255, 255, 255, 0.9)", fontWeight: "500" }}>
                      {experience.duration}
                    </span>
                  </div>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "8px 15px",
                    background: "rgba(56, 249, 215, 0.1)",
                    borderRadius: "20px",
                    border: "1px solid rgba(56, 249, 215, 0.2)"
                  }}>
                    <FaMapMarkerAlt style={{ color: "#c770f0", fontSize: "0.9em" }} />
                    <span style={{ fontSize: "0.9em", color: "rgba(255, 255, 255, 0.9)", fontWeight: "500" }}>
                      {experience.location}
                    </span>
                  </div>
                </div>

                <p style={{
                  color: "rgba(255, 255, 255, 0.9)",
                  fontSize: "1.05em",
                  lineHeight: "1.7",
                  marginBottom: "30px",
                  fontStyle: "italic"
                }}>
                  {experience.description}
                </p>

                {/* Stats Section */}
                {experience.stats && (
                  <div style={{
                    display: "flex",
                    justifyContent: "space-around",
                    marginBottom: "30px",
                    padding: "20px",
                    background: "rgba(199, 112, 240, 0.05)",
                    borderRadius: "15px",
                    border: "1px solid rgba(199, 112, 240, 0.1)"
                  }}>
                    {Object.entries(experience.stats).map(([key, value], idx) => (
                      <div key={idx} style={{ textAlign: "center" }}>
                        <div style={{ fontSize: "1.3em", fontWeight: "700", color: "#38f9d7", filter: "drop-shadow(0 0 5px rgba(56, 249, 215, 0.5))" }}>
                          {value}
                        </div>
                        <div style={{ fontSize: "0.7em", color: "rgba(255, 255, 255, 0.7)", textTransform: "capitalize", fontWeight: "500" }}>
                          {key}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ marginBottom: "30px" }}>
                  <h5 style={{
                    color: "#c770f0",
                    fontSize: "1.2em",
                    marginBottom: "20px",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px"
                  }}>
                    <FaUsers style={{ fontSize: "1em" }} />
                    Key Responsibilities:
                  </h5>
                  <ul style={{
                    listStyle: "none",
                    padding: 0,
                    margin: 0
                  }}>
                    {experience.responsibilities.map((responsibility, index) => (
                      <li key={index} style={{
                        marginBottom: "12px",
                        display: "flex",
                        alignItems: "flex-start"
                      }}>
                        <span style={{
                          color: "#38f9d7",
                          marginRight: "12px",
                          fontSize: "0.8em",
                          marginTop: "3px",
                          filter: "drop-shadow(0 0 5px rgba(56, 249, 215, 0.5))"
                        }}>
                          ▸
                        </span>
                        <span style={{
                          fontSize: "0.95em",
                          lineHeight: "1.6",
                          color: "rgba(255, 255, 255, 0.9)"
                        }}>
                          {responsibility}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div style={{ marginBottom: "30px" }}>
                  <h5 style={{
                    color: "#c770f0",
                    fontSize: "1.2em",
                    marginBottom: "20px",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px"
                  }}>
                    <FaChartLine style={{ fontSize: "1em" }} />
                    Technologies:
                  </h5>
                  <div style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "10px"
                  }}>
                    {experience.technologies.map((tech, index) => (
                      <span 
                        key={index} 
                        className="tech-tag"
                        style={{
                          background: "linear-gradient(135deg, rgba(199, 112, 240, 0.15) 0%, rgba(199, 112, 240, 0.05) 100%)",
                          color: "#c770f0",
                          padding: "8px 16px",
                          borderRadius: "20px",
                          fontSize: "0.85em",
                          border: "1px solid rgba(199, 112, 240, 0.3)",
                          fontWeight: "500",
                          transition: "all 0.3s ease",
                          cursor: "pointer"
                        }}
                        onMouseEnter={(e) => {
                          e.stopPropagation();
                          e.target.style.transform = "scale(1.03)";
                          e.target.style.background = "linear-gradient(135deg, rgba(199, 112, 240, 0.25) 0%, rgba(199, 112, 240, 0.1) 100%)";
                        }}
                        onMouseLeave={(e) => {
                          e.stopPropagation();
                          e.target.style.transform = "scale(1)";
                          e.target.style.background = "linear-gradient(135deg, rgba(199, 112, 240, 0.15) 0%, rgba(199, 112, 240, 0.05) 100%)";
                        }}
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <h5 style={{
                    color: "#c770f0",
                    fontSize: "1.2em",
                    marginBottom: "20px",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px"
                  }}>
                    <FaAward style={{ fontSize: "1em" }} />
                    Key Achievements:
                  </h5>
                  <div style={{
                    background: "linear-gradient(135deg, rgba(56, 249, 215, 0.1) 0%, rgba(56, 249, 215, 0.05) 100%)",
                    border: "1px solid rgba(56, 249, 215, 0.2)",
                    borderRadius: "15px",
                    padding: "20px"
                  }}>
                    <ul style={{
                      listStyle: "none",
                      padding: 0,
                      margin: 0
                    }}>
                      {experience.achievements.map((achievement, index) => (
                        <li key={index} style={{
                          marginBottom: "10px",
                          display: "flex",
                          alignItems: "flex-start"
                        }}>
                          <span style={{
                            color: "#38f9d7",
                            marginRight: "12px",
                            fontSize: "0.8em",
                            marginTop: "3px",
                            filter: "drop-shadow(0 0 5px rgba(56, 249, 215, 0.5))"
                          }}>
                            ▸
                          </span>
                          <span style={{
                            fontSize: "0.95em",
                            lineHeight: "1.6",
                            color: "rgba(56, 249, 215, 0.95)",
                            fontWeight: "500"
                          }}>
                            {achievement}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </Container>
    </Container>
  );
}

export default React.memo(ProfessionalExperience);
